//ReadTouchBook.js
import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPlayCircle, faStop, faVolumeMute, faVolumeUp, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Speech from 'speak-tts';
import './styles/ReadBook.css';

// ✅ Correct path matching your public folder structure
const backgroundMusic = process.env.PUBLIC_URL + '/assets/sounds/AnimalMovieRingtone.mp3';

const ReadTouchBook = () => {
  const authState = useSelector(state => state.authReducer);
  const [userId, setUserId] = useState(authState.user._id);

  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();

  const location = useLocation();

  const bookMasterId = location.state.bookMasterId;
  const bookCollectionName = location.state.bookCollectionName;
  const pageNumber = location.state.lastPageRead;
  const bookPages = location.state.bookPages;

  const [pageContent, setPageContent] = useState('');
  const [splText, setSplText] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [bookImageMasterId, setBookImageMasterId] = useState('');
  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber);
  const [hasSpeech, setHasSpeech] = useState(0);
  const [hasMusic, setHasMusic] = useState(0);
  const [hasVideo, setHasVideo] = useState(0);
  const [videoUrl, setVideoUrl] = useState(0);
  const [readFromSplText, setReadFromSplText] = useState(0);
  const [musicAudio] = useState(new Audio(backgroundMusic));
  const [pageCount, setPageCount] = useState(bookPages);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [dateRead, setDateRead] = useState(new Date());
  const [pageLoadTime, setPageLoadTime] = useState(null);
  const [readTimeInMiliseconds, setReadTimeInMiliseconds] = useState(0);
  const [readingRemarks, setReadingRemarks] = useState('');
  const [isLogSaved, setIsLogSaved] = useState(true);
  const [isInteracted, setIsInteracted] = useState(false);

  const [masterImageData, setMasterImageData] = useState('');
  const [readFromImageFile, setReadFromImageFile] = useState(0);
  const [masterImagePath, setMasterImagePath] = useState('');

  const [error, setError] = useState(false);

  useEffect(() => {
    setPageLoadTime(new Date());
  }, []);


// MasterImage
const fetchMasterImage = useCallback(() => {
  const config = {
    url: "/api/bookimagemaster",
    method: "get",
    headers: { Authorization: authState.token },
    data: { bookMasterId }
  };

  fetchData(config, { showSuccessToast: false })
    .then(response => {
      // console.log("Response:", response);
      if (response && response.imageData) {
        setMasterImageData(response.imageData);
        setReadFromImageFile(response.readFromImageFile);
        setMasterImagePath(response.imagePath);
        setError(null);
      } else {
        console.error("Invalid response format: Image master list not found in response");
      }
    })
    .catch(error => {
      console.error("Error fetching book image master:", error);
    });
}, [authState.token, bookMasterId]);

// useEffect for fetching book image, runs only once on mount
useEffect(() => {
  if (!authState.isLoggedIn) return;
  // console.log("fetchMasterImage called");
  // console.log("bookMasterId",bookMasterId);
  fetchMasterImage();
}, []);



  // TouchBookPage
  const fetchBookPage = useCallback(() => {
    const config = {
      url: "/touchbook",
      method: "post",
      headers: { Authorization: authState.token },
      data: {
        bookMasterId,
        bookCollectionName,
        currentPageNumber,
      }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        // console.log("Response:", response);
        // console.log("videoUrl", response.videoURL);
        if (response && response.pageContent) {
          setPageContent(response.pageContent);
          setSplText(response.splText);
          setBookTitle(response.bookTitle);
          setHasSpeech(response.hasSpeech);
          setHasMusic(response.hasMusic);
          setHasVideo(response.hasVideo);
          setVideoUrl(response.videoURL);
          setBookImageMasterId(response.imageMasterId);
          if ('readFromSplText' in response) {
            setReadFromSplText(response.readFromSplText);
          } else {
            setReadFromSplText(0);
          }
          setError(null);
        } else {
          console.error("Invalid response format: TouchBook content not found in response");
        }
      })
      .catch(error => {
        console.error("Error fetching book page:", error);
        console.error("Error fetching set masters:", error);
      });
  }, [authState.token, bookMasterId, bookCollectionName, currentPageNumber]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchBookPage();
  }, [authState.isLoggedIn, fetchBookPage, location.state.bookMasterId, location.state.bookCollectionName, location.state.lastPageRead]);



  // Save Read Log
  const saveReadLog = async (remarks) => {
    try {
      const response = await fetch("/api/myreadinglog", {
        method: "POST",
        headers: {
          Authorization: authState.token,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          bookId: bookMasterId,
          userId: userId,
          dateRead: dateRead,
          pageNumber: currentPageNumber,
          readTimeInMiliseconds: readTimeInMiliseconds,
          readingRemarks: remarks,
          isActive: 1,
          isDeleted: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update app like');
      }

      const data = await response.json();
      setSuccessMessage(true);
      setReadTimeInMiliseconds(0);
      setPageLoadTime(new Date());
    } catch (error) {
      console.error("Error updating app like:", error);
    }
  };

  const saveReadLogIfNeeded = () => {
    if (isInteracted && !isLogSaved) {
      const elapsedTime = currentTime - pageLoadTime;
      setReadTimeInMiliseconds(elapsedTime);
      const currentTime = new Date();
      setReadTimeInMiliseconds(elapsedTime);
      saveReadLog(readingRemarks); // Save reading remarks
      setIsLogSaved(true);
    }
  };

  const handleNextPage = () => {
    setReadingRemarks("Next button clicked");
    let currentTime = new Date();
    const elapsedTime = currentTime - pageLoadTime;
    setReadTimeInMiliseconds(elapsedTime);
    saveReadLog("Next");
    currentTime = new Date();
    setPageLoadTime(currentTime);
    if (currentPageNumber < pageCount) {
      speech.cancel();
      setIsSpeaking(false);
      setCurrentPageNumber(currentPageNumber + 1);
    }
  };

  useEffect(() => {
    // console.log("Setting use effect");
    setReadTimeInMiliseconds();
    setReadingRemarks();
    setPageLoadTime(new Date());
  }, []);

  const handlePrevPage = () => {
    setReadingRemarks("Prev button clicked");
    let currentTime = new Date();
    const elapsedTime = currentTime - pageLoadTime;
    setReadTimeInMiliseconds(elapsedTime);
    saveReadLog("Previous");
    currentTime = new Date();
    setPageLoadTime(currentTime);
    if (currentPageNumber > 1) {
      speech.cancel();
      setIsSpeaking(false);
      setCurrentPageNumber(currentPageNumber - 1);
    }
  };

  useEffect(() => {
    setReadTimeInMiliseconds();
    setReadingRemarks();
    setPageLoadTime(new Date());
  }, []);

  const handleSwipe = () => {
    setIsInteracted(true);
    setReadingRemarks("Swiped"); // Set reading remarks
    saveReadLogIfNeeded(); // Call saveReadLogIfNeeded
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      handleSwipe();
      handleNextPage();
    },
    onSwipedRight: () => {
      handleSwipe();
      handlePrevPage();
    },
  });

  const handleClose = () => {
    setReadingRemarks("Close button clicked");
    const currentTime = new Date();
    const elapsedTime = currentTime - pageLoadTime;
    setReadTimeInMiliseconds(elapsedTime);
    const remarks = "Closed";
    saveReadLog(remarks).then(() => {
      window.location.href = '/touchbook-home';
    }).catch(error => {
      console.error("Error saving log:", error);
    });
  };

  useEffect(() => {
    // console.log("Setting initial values");
    setReadTimeInMiliseconds(0);
    setPageLoadTime(new Date());
  }, []);


  const speech = new Speech();

  const toggleSpeech = () => {
    if (isSpeaking) {
      speech.cancel();
      setIsSpeaking(false);
    } else {
      setIsSpeaking(true);
      speech.setRate(0.8); // Set speech rate to 1.5 times the normal speed
      speech.setPitch(1.2); // Set pitch to 1.2 (higher pitch)
      speech.setVolume(1.0); // Set volume to 100%
      speech.setLanguage('hi-IN'); // Set language 

      if (readFromSplText === 1) {
        speech.speak({ text: splText });
      } else {
        speech.speak({ text: pageContent });
      }
    }
  };

  // useEffect(() => {
  //   const cleanup = () => {
  //     if (isSpeaking) {
  //       speech.cancel();
  //       setIsSpeaking(false);
  //     }
  //   };

  //   return cleanup;
  // }, [isSpeaking]);

  useEffect(() => {
    return () => {
      if (isSpeaking) {
        speech.cancel();
        setIsSpeaking(false);
      }
    };
  }, [isSpeaking]);


  const toggleMusic = () => {
    if (isListening) {
      musicAudio.pause();
    } else {
      musicAudio.play();
    }

    setIsListening(!isListening);
  };

  musicAudio.addEventListener('ended', () => {
    if (isListening) {
      musicAudio.currentTime = 0;
      musicAudio.play();
    }
  });

  const watchVideo = () => {
    if (videoUrl) {
      window.open(videoUrl, '_blank');
    } else {
      console.error('Video URL is not provided.');
    }
  };


  return (
    <div className="readbook-container my-2 mx-auto max-w-[750px] py-4 bg-white text-black fixed-height-container" style={{ height: '100vh', overflow: 'auto' }} {...swipeHandlers}> {/* Apply swipe handlers to the main container */}
      {loading ? (
        <div className="spinner-container">
          <div className="spinner">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        </div>
      ) : (
        <>
          <div className="fixed-height-container" style={{ flex: 1, overflowY: 'auto' }}>
            <div className="flex justify-between items-center mt-2 mb-2 bg-cyan-900 text-white">
              <div style={{ width: '90%', maxHeight: '40px', overflow: 'hidden' }}>
                <h3 className="text-lg mt-1 mb-4 mx-2 max-w-[750px] bg-cyan-900 text-white">
                  <i className="fa fa-book-open" aria-hidden="true"></i>   {bookTitle}
                </h3>
              </div>
              <div style={{ width: '10%', maxHeight: '40px', overflow: 'hidden' }}>
                <button className="mx-4  bg-cyan-900" onClick={handleClose}>
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            <div class="flex justify-center">
              <button className="mr-4" onClick={toggleSpeech} disabled={hasSpeech === 0}>
                {isSpeaking ? <FontAwesomeIcon icon={faStop} /> : <FontAwesomeIcon icon={faPlay} />}
              </button>
              <button className="mr-4" onClick={toggleMusic} disabled={hasMusic === 0}>
                {isListening ?
                  <FontAwesomeIcon icon={faVolumeUp} /> :
                  <FontAwesomeIcon icon={faVolumeMute} />
                }
              </button>
              <button class="mr-4" onClick={watchVideo} disabled={hasVideo === 0}><FontAwesomeIcon icon={faPlayCircle} /></button>
            </div>
            {/* <div className='page-content' style={{ minHeight: '100%' }} */}
            {/* dangerouslySetInnerHTML={{ __html: masterImageData }}/> */}
            {/* Swipe handlers to the content * */}
            {/* <div className='page-content' style={{ minHeight: '100%' }} */}
            {/* dangerouslySetInnerHTML={{ __html: pageContent }} */}
            {/* {...swipeHandlers} />  */}
            <div className="container">
              <div className="static-content height-60">
                <div dangerouslySetInnerHTML={{ __html: readFromImageFile }} />
              </div>
              <div className="swipable-content height-40" {...swipeHandlers}>
                <div dangerouslySetInnerHTML={{ __html: pageContent }} />
              </div>
            </div>
            <div className='mt-2 mb-2  bg-cyan-900 text-white align-middle' style={{ textAlign: 'center' }}>
              Current Page: {currentPageNumber}
            </div>
            <div className="page-navigation bg-cyan-900 text-white" style={{ textAlign: 'center', display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ width: '20%' }}>
                <button title="Prev" className="tooltip-button" onClick={handlePrevPage} disabled={currentPageNumber === 1}>
                  <i className="fa fa-angle-left ml-2 mr-2" aria-hidden="true"></i>
                </button>
              </div>
              <div style={{ width: '40%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span>Page</span>
                <span style={{ margin: '0 5px' }}></span>
                <select
                  className='bg-cyan-900 mt-4 border-solid border-2 border-cyan-900'
                  value={currentPageNumber}
                  onChange={(e) => setCurrentPageNumber(parseInt(e.target.value))}
                >
                  {[...Array(pageCount).keys()].map(page => (
                    <option key={page + 1} value={page + 1}>{page + 1}</option>
                  ))}
                </select>
              </div>
              <div style={{ width: '20%' }}>
                <button title="Next" className="tooltip-button" onClick={handleNextPage} disabled={currentPageNumber === pageCount}>
                  <i className="fa fa-angle-right ml-2 mr-2" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReadTouchBook;
